import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useRef, useState } from "react";
import i18n from "../../../../utils/i18n";
import classes from "./searchFlowFooter.module.scss";

const VALIDATION_HINT_TEXT = i18n.t("searchflow.validationhint");
const VALIDATION_HINT_DURATION_MS = 2500;

const SearchFlowFooter = ({
  onBack,
  onNext,
  nextDisabled = false,
  nextLoading = false,
  isTransitioning = false,
  nextVariant = "default",
  nextLabel = i18n.t("general.forwardbutton"),
  layout = "default",
  footerFullWidth = false,
  embedded = false,
  centerContent = null,
}) => {
  const [showValidationHint, setShowValidationHint] = useState(false);
  const hintTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (hintTimeoutRef.current) {
        clearTimeout(hintTimeoutRef.current);
      }
    },
    [],
  );

  if (!onBack && !onNext) {
    return null;
  }

  const backDisabled = isTransitioning;
  const forwardDisabled = isTransitioning || nextDisabled;

  const handleBack = () => {
    if (backDisabled) {
      return;
    }
    onBack?.();
  };

  const handleNext = () => {
    if (forwardDisabled) {
      if (nextDisabled && !isTransitioning) {
        setShowValidationHint(true);
        if (hintTimeoutRef.current) {
          clearTimeout(hintTimeoutRef.current);
        }
        hintTimeoutRef.current = setTimeout(() => {
          setShowValidationHint(false);
        }, VALIDATION_HINT_DURATION_MS);
      }
      return;
    }
    setShowValidationHint(false);
    onNext?.();
  };

  const showValidationHintNow = () => {
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
      hintTimeoutRef.current = null;
    }
    setShowValidationHint(true);
  };

  const hideValidationHintNow = () => {
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
      hintTimeoutRef.current = null;
    }
    setShowValidationHint(false);
  };

  const handleNextHoverStart = () => {
    if (!nextDisabled || isTransitioning) {
      return;
    }
    showValidationHintNow();
  };

  const handleNextHoverEnd = () => {
    hideValidationHintNow();
  };

  const continueButtonClassName = (() => {
    if (footerFullWidth) {
      return classes.continueButtonFullWidth;
    }
    if (layout === "centered") {
      return classes.continueButtonCentered;
    }
    return classes.continueButton;
  })();

  const continueButton = onNext ? (
    <Tooltip
      title={VALIDATION_HINT_TEXT}
      open={showValidationHint}
      arrow
      placement="top"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 150 }}
      classes={{ tooltip: classes.validationHint, arrow: classes.validationHintArrow }}
    >
      <button
        type="button"
        className={continueButtonClassName}
        onClick={handleNext}
        onMouseEnter={handleNextHoverStart}
        onMouseLeave={handleNextHoverEnd}
        onFocus={handleNextHoverStart}
        onBlur={handleNextHoverEnd}
        aria-disabled={forwardDisabled}
      >
        <span>{nextLabel}</span>
        <ChevronRight sx={{ fontSize: 20, color: "#fff" }} />
      </button>
    </Tooltip>
  ) : null;

  if (embedded && layout === "centered") {
    return onNext ? (
      <div
        className={
          footerFullWidth ? classes.embeddedFullWidth : classes.embeddedCentered
        }
      >
        {continueButton}
      </div>
    ) : null;
  }

  const backControl = onBack ? (
    <button
      type="button"
      className={classes.backButton}
      onClick={handleBack}
      disabled={backDisabled}
      aria-label={i18n.t("general.backbutton")}
    >
      <ChevronLeft sx={{ fontSize: 28, color: "#0f172a", height: "20px", width: "auto" }} />
    </button>
  ) : (
    <span className={classes.backSpacer} aria-hidden="true" />
  );

  const nextControl = onNext ? (
    nextLoading ? (
      <button
        type="button"
        className={classes.nextButtonLoading}
        disabled
        aria-label={i18n.t("searchflow.loadingaria")}
      >
        <CircularProgress size={20} sx={{ color: "#1c32f3" }} />
      </button>
    ) : nextVariant === "continue" ? (
      continueButton
    ) : (
      <Tooltip
        title={VALIDATION_HINT_TEXT}
        open={showValidationHint}
        arrow
        placement="top"
        disableHoverListener
        disableFocusListener
        disableTouchListener
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 150 }}
        classes={{ tooltip: classes.validationHint, arrow: classes.validationHintArrow }}
      >
        <button
          type="button"
          className={classes.nextButton}
          onClick={handleNext}
          onMouseEnter={handleNextHoverStart}
          onMouseLeave={handleNextHoverEnd}
          onFocus={handleNextHoverStart}
          onBlur={handleNextHoverEnd}
          aria-disabled={forwardDisabled}
        >
          {nextLabel}
        </button>
      </Tooltip>
    )
  ) : null;

  const content = centerContent ? (
    <>
      <div className={classes.startSlot}>{backControl}</div>
      <div className={classes.centerSlot}>{centerContent}</div>
      <div className={classes.endSlot}>{nextControl}</div>
    </>
  ) : (
    <>
      {backControl}
      {nextControl}
    </>
  );

  if (embedded) {
    const embeddedRowClassName = centerContent
      ? classes.embeddedRowWithCenter
      : classes.embeddedRow;

    return <div className={embeddedRowClassName}>{content}</div>;
  }

  return <footer className={classes.footer}>{content}</footer>;
};

export default SearchFlowFooter;
